import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, CheckCircle2, TrendingUp, ShieldCheck, FileText } from 'lucide-react';

/**
 * Standard product-page hero used across the Videra product pages.
 * Clean light aesthetic + a hero screenshot that dominates the fold,
 * with 2-3 optional "live" chips that gently animate in over the image
 * to give the feel of a working product.
 *
 * Keep the body prop to ONE short sentence, the visual carries the weight.
 */
const CHIP_ICONS = {
  check: CheckCircle2,
  trend: TrendingUp,
  shield: ShieldCheck,
  doc: FileText,
};

const ProductHero = ({
  eyebrow,
  title,
  subtitle,
  body,
  primary,      // { label, to?, href? }
  secondary,    // { label, to?, href? }
  supportingLine,
  heroImage,    // optional /images/... URL
  heroImageAlt = '',
  liveChips = [],  // [{ icon: 'check'|'trend'|'shield'|'doc', label: 'string', pos: 'tl'|'tr'|'bl'|'br' }]
  testid,
}) => {
  const renderCta = (cta, dark = true) => {
    if (!cta) return null;
    const cls = dark
      ? 'inline-flex items-center justify-center bg-slate-900 text-white px-7 py-3.5 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors group'
      : 'inline-flex items-center justify-center bg-white border border-slate-300 text-slate-900 px-7 py-3.5 rounded-md text-sm font-medium hover:border-slate-900 transition-colors';
    const inner = (
      <>
        {cta.label}
        {dark ? (
          <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
        ) : (
          <ChevronRight className="ml-1 w-4 h-4" />
        )}
      </>
    );
    if (cta.href) {
      return (
        <a href={cta.href} className={cls} data-testid={cta.testid}>
          {inner}
        </a>
      );
    }
    return (
      <Link to={cta.to} className={cls} data-testid={cta.testid}>
        {inner}
      </Link>
    );
  };

  const chipPos = {
    tl: 'top-4 left-4 md:top-6 md:left-6',
    tr: 'top-4 right-4 md:top-6 md:right-6',
    bl: 'bottom-4 left-4 md:bottom-6 md:left-6',
    br: 'bottom-4 right-4 md:bottom-6 md:right-6',
  };
  const chipDelay = ['0.6s', '1.1s', '1.6s', '2.1s'];

  const hasImage = Boolean(heroImage);

  return (
    <section className="relative pt-16 pb-14 md:pt-24 md:pb-20 overflow-hidden" data-testid={testid}>
      <div className="absolute inset-0 bg-gradient-to-b from-slate-50 via-white to-white" />
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, #0f172a 1px, transparent 0)',
        backgroundSize: '24px 24px',
      }} />
      <div className={`relative mx-auto px-4 sm:px-6 lg:px-8 ${hasImage ? 'max-w-7xl' : 'max-w-5xl'}`}>
        <div className={hasImage ? 'grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-14 items-center' : ''}>
          <div className="animate-[videra-hero-in_.6s_ease-out]">
            {eyebrow && (
              <p className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold mb-5">{eyebrow}</p>
            )}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 leading-[1.05]">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-5 text-lg lg:text-xl text-slate-500 leading-relaxed max-w-3xl">
                {subtitle}
              </p>
            )}
            {body && (
              <div className="mt-6 text-base lg:text-lg text-slate-600 leading-relaxed max-w-2xl space-y-3">
                {typeof body === 'string' ? <p>{body}</p> : body}
              </div>
            )}
            {(primary || secondary) && (
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                {renderCta(primary, true)}
                {renderCta(secondary, false)}
              </div>
            )}
            {supportingLine && (
              <p className="mt-6 text-sm text-slate-500">{supportingLine}</p>
            )}
          </div>
          {hasImage && (
            <div className="relative animate-[videra-hero-image-in_.8s_.15s_ease-out_both]">
              <div className="relative bg-slate-900 rounded-2xl p-2 shadow-[0_30px_80px_-25px_rgba(15,23,42,0.55)] ring-1 ring-slate-900/5 rotate-[-0.3deg]">
                <img
                  src={heroImage}
                  alt={heroImageAlt}
                  loading="eager"
                  className="w-full aspect-[16/11] object-cover object-top rounded-lg"
                />
                {/* Floating "live" chips */}
                {liveChips.slice(0, 4).map((chip, i) => {
                  const Icon = CHIP_ICONS[chip.icon] || CheckCircle2;
                  return (
                    <div
                      key={i}
                      className={`absolute ${chipPos[chip.pos || 'tr']} bg-white/95 backdrop-blur-sm rounded-lg shadow-lg ring-1 ring-slate-900/5 px-3 py-2 flex items-center gap-2 animate-[videra-chip-in_.5s_ease-out_both]`}
                      style={{ animationDelay: chipDelay[i] || '0.6s' }}
                    >
                      <div className={`w-6 h-6 rounded-md flex items-center justify-center ${chip.tone === 'amber' ? 'bg-amber-50 text-amber-600' : chip.tone === 'red' ? 'bg-red-50 text-red-600' : chip.tone === 'emerald' ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600'}`}>
                        <Icon className="w-3.5 h-3.5" />
                      </div>
                      <div>
                        {chip.kicker && (
                          <p className="text-[9px] uppercase tracking-[0.14em] text-slate-500 font-semibold leading-none">{chip.kicker}</p>
                        )}
                        <p className="text-xs font-medium text-slate-900 leading-tight mt-0.5">{chip.label}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Soft ambient glow behind screenshot */}
              <div className="absolute -inset-6 -z-10 bg-blue-500/10 blur-3xl rounded-full" aria-hidden="true" />
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes videra-hero-in {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes videra-hero-image-in {
          from { opacity: 0; transform: translateY(24px) rotate(-0.6deg); }
          to { opacity: 1; transform: translateY(0) rotate(-0.3deg); }
        }
        @keyframes videra-chip-in {
          from { opacity: 0; transform: translateY(6px) scale(.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </section>
  );
};

export default ProductHero;
